import type { GanttVisualizationProfileDto } from '../api';

export type ZoomLevel = '5min' | '10min' | '15min' | '30min' | '60min' | '120min';

/** Interval in minutes per zoom level (5, 10, 15, 30, 60, 120). Used by Schedule and Gantt renderers. Single source of truth. */
export const ZOOM_LEVEL_INTERVAL_MINUTES: Record<ZoomLevel, number> = {
  '5min': 5,
  '10min': 10,
  '15min': 15,
  '30min': 30,
  '60min': 60,
  '120min': 120
};

const VALID_ZOOM_LEVELS: Set<string> = new Set(Object.keys(ZOOM_LEVEL_INTERVAL_MINUTES));

/** Returns interval minutes for the given ZoomLevel. Falls back to 10 if unknown. */
export function getZoomIntervalMinutes(zoomLevel: string | undefined): number {
  return (zoomLevel != null && VALID_ZOOM_LEVELS.has(zoomLevel)
    ? ZOOM_LEVEL_INTERVAL_MINUTES[zoomLevel as ZoomLevel]
    : ZOOM_LEVEL_INTERVAL_MINUTES['10min']);
}

export interface ScheduleOperation {
  id: string | number;
  subject: string;
  operationName?: string;
  campaignId?: string;
  startTime: Date;
  endTime: Date;
  resourceId: string | number;
  color?: string;
  orderUuid?: string;
  orderNumber?: string;
  productName?: string;
}

export interface ScheduleSetup {
  id: string | number;
  subject: string;
  startTime: Date;
  endTime: Date;
  resourceId: string | number;
  color?: string;
  relatedOperationId?: string | number;
  productName?: string;
  /** Gantt sequence filter: both must be visible for CHANGEOVER setups */
  precedingOrderUuid?: string;
  succeedingOrderUuid?: string;
  /** True when row is secondary/conditional resource (not a product changeover) */
  isAdditionalResourceBar?: boolean;
  /** For additional-resource bars: product filter by material */
  materialUuid?: string;
}

export interface ScheduleResourceShift {
  start: Date;
  end: Date;
  label?: string;
  type?: 'regular' | 'exception' | 'off';
}

export interface ScheduleResource {
  id: string | number;
  name: string;
  color?: string;
  type?: string;
  shifts?: ScheduleResourceShift[];
}

/**
 * Common props for all schedule renderers (Schedule, Gantt, future implementations).
 * Use this interface when implementing new renderers.
 */
export interface ProductionScheduleRendererProps {
  timeWindow: { start: Date; end: Date };
  now?: Date;
  operations: ScheduleOperation[];
  setups?: ScheduleSetup[];
  resources: ScheduleResource[];
  zoomLevel?: ZoomLevel;
  visualProfile: GanttVisualizationProfileDto;
  onVisualProfileChange?: (profile: GanttVisualizationProfileDto) => void;
  onZoomToFit?: () => void;
  height?: string;
  width?: string;
  readonly?: boolean;
}

export interface ProductionScheduleViewHandle {
  scrollToStart: () => void;
  scrollToEnd: () => void;
  scrollToNow: () => void;
  zoomToFit: () => void;
  setVisualProfile: (profile: GanttVisualizationProfileDto) => void;
}

export type ProductionScheduleRendererType = 'schedule' | 'gantt';
